import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MeetingRepository } from 'repository/meetingRepository';
import { IcClose, IcClock, IcUsers, IcCheck } from 'components/common/icons/EmojiIcons';
import styles from './InviteFromPastMeetings.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

/**
 * 과거 모임의 멤버를 골라서 현재 모임에 일괄 초대.
 * - 과거 모임 목록 조회 → 모임 선택 → 멤버 체크리스트 → invite API 호출
 * - 서버가 각 초대 대상에게 MEETING_INVITED 푸시를 자동 전송 (기존 구현)
 */

interface Props {
    open: boolean;
    onClose: () => void;
    /** 초대 대상 모임 (지금 만든 모임). onInvite override 시엔 단순 식별자로만 사용 (목록 필터링) */
    targetMeetingId: string;
    /** 이미 targetMeeting 에 있는 userNo 들 — 중복 초대 방지용 (선택) */
    excludeUserNos?: number[];
    /** 현재 로그인 유저 — 자기 자신 제외용 */
    currentUserNo?: number;
    /** 초대 전송이 성공한 직후 호출 (대기자 목록 갱신 트리거용) */
    onInviteSent?: (sentUserNos: number[]) => void;
    /** 초대 API 를 외부에서 주입 — 번개 초대 등 다른 도메인에서 재사용. 미지정 시 모임 초대 API 호출 */
    onInvite?: (userNos: number[]) => Promise<boolean>;
    /** 모달 헤더 — 도메인별 라벨 변경 가능. 미지정 시 모임 초대 라벨 */
    titleStep1?: string;
    titleStep2?: string;
    /** 성공 토스트 텍스트 — n 명 치환은 호출자가 직접 (인자로 count 받기 전 단순 string) */
    successToast?: (count: number) => string;
}

type PastMeeting = {
    id: string;
    title: string;
    date?: string;
    memberCount?: number;
};

type Member = {
    userNo: number;
    nickname: string;
    profileImgUrl?: string;
    role?: string; // CREATOR / MEMBER
};

// 서버 응답이 profileUrl / profileImgUrl / profileImgPath 중 아무거나 쓸 수 있어 모두 체크
const pickProfile = (m: any): string | undefined =>
    m?.profileUrl || m?.profileImgUrl || m?.profileImgPath || undefined;

const decodeNick = (raw: any): string => {
    const s = raw || '';
    try { return decodeURIComponent(s); } catch { return s; }
};

export const InviteFromPastMeetings: React.FC<Props> = ({
    open, onClose, targetMeetingId, excludeUserNos = [], currentUserNo, onInviteSent,
    onInvite, titleStep1, titleStep2, successToast,
}) => {
    const { t } = useTranslation();
    const [past, setPast] = useState<PastMeeting[]>([]);
    const [isLoadingPast, setIsLoadingPast] = useState(false);
    const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
    const [members, setMembers] = useState<Member[]>([]);
    const [isLoadingMembers, setIsLoadingMembers] = useState(false);
    const [checkedUserNos, setCheckedUserNos] = useState<Set<number>>(new Set());
    const [isSending, setIsSending] = useState(false);
    const [toast, setToast] = useState<string>('');
    // 모임별 멤버 캐시 (목록 카드에 프로필 미리보기용)
    const [memberCache, setMemberCache] = useState<Record<string, Member[]>>({});

    useEffect(() => {
        if (!open) return;
        setIsLoadingPast(true);
        MeetingRepository.onGetMyMeetings({ ended: true, page: 1, pageSize: 30 })
            .then((res: any) => {
                const list = res?.data?.data?.list || res?.data?.data || [];
                const mapped: PastMeeting[] = list
                    .filter((m: any) => m.meetingId && m.meetingId !== targetMeetingId)
                    .map((m: any) => ({
                        id: m.meetingId,
                        title: m.meetingName || t('invitePast.unnamedMeeting'),
                        date: m.schedule?.endDatetime
                            ? new Date(m.schedule.endDatetime).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })
                            : undefined,
                        memberCount: m.memberCount || undefined,
                    }));
                setPast(mapped);

                // 상단 10개 모임에 대해 멤버 병렬 프리페치 (목록 카드 프로필 미리보기)
                mapped.slice(0, 10).forEach(pm => {
                    MeetingRepository.onGetMeetingMembers(pm.id)
                        .then((r: any) => {
                            const mlist = r?.data?.data || r?.data || [];
                            const mapped2: Member[] = mlist.map((m: any) => ({
                                userNo: Number(m.userNo),
                                nickname: decodeNick(m.nickname),
                                profileImgUrl: pickProfile(m),
                                role: m.role,
                            })).filter((m: Member) => m.userNo > 0);
                            setMemberCache(prev => ({ ...prev, [pm.id]: mapped2 }));
                        })
                        .catch(() => { /* 실패해도 조용히 무시 — 카드에 프로필 preview 만 없을 뿐 */ });
                });
            })
            .catch(() => setPast([]))
            .finally(() => setIsLoadingPast(false));
    }, [open, targetMeetingId]);

    useEffect(() => {
        if (!selectedMeetingId) { setMembers([]); return; }
        setIsLoadingMembers(true);
        // 캐시에 있으면 바로 사용 — closure 로 최신 값 읽음 (deps 에 memberCache 넣으면 무한 loop)
        const cached = memberCache[selectedMeetingId];
        const applyMembers = (list: any[]) => {
            const excluded = new Set([...excludeUserNos, ...(currentUserNo != null ? [currentUserNo] : [])]);
            const mapped: Member[] = list
                .map((m: any) => ({
                    userNo: Number(m.userNo),
                    nickname: decodeNick(m.nickname),
                    profileImgUrl: pickProfile(m),
                    role: m.role,
                }))
                .filter((m: Member) => m.userNo > 0 && !excluded.has(m.userNo));
            setMembers(mapped);
            setCheckedUserNos(new Set(mapped.map((m: Member) => m.userNo)));
            setIsLoadingMembers(false);
        };
        if (cached) { applyMembers(cached); return; }
        MeetingRepository.onGetMeetingMembers(selectedMeetingId)
            .then((res: any) => applyMembers(res?.data?.data || res?.data || []))
            .catch(() => { setMembers([]); setIsLoadingMembers(false); });
        // deps 에서 excludeUserNos / currentUserNo / memberCache 제거 — 부모가 매 render 마다
        // 새 [] 를 default prop 으로 넘기는 reference 차이로 effect 가 무한 재실행 + setMembers 가
        // 부모 re-render 트리거 → React error #185 (Maximum update depth) 회귀.
        // closure 로 최신 값 읽음 (selectedMeetingId 변경 시에만 fetch).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMeetingId]);

    const toggleMember = (userNo: number) => {
        setCheckedUserNos(prev => {
            const next = new Set(prev);
            if (next.has(userNo)) next.delete(userNo);
            else next.add(userNo);
            return next;
        });
    };

    const toggleAll = () => {
        if (checkedUserNos.size === members.length) setCheckedUserNos(new Set());
        else setCheckedUserNos(new Set(members.map(m => m.userNo)));
    };

    const handleInvite = async () => {
        const list = Array.from(checkedUserNos);
        if (list.length === 0) return;
        setIsSending(true);
        try {
            // onInvite 가 주입되면 외부 API 호출 (예: 번개 초대), 아니면 기본 모임 초대 API
            let ok = true;
            if (onInvite) {
                ok = await onInvite(list);
            } else {
                await MeetingRepository.onPostInviteToMeeting(targetMeetingId, { inviteeUserNos: list });
            }
            if (ok) {
                setToast(successToast ? successToast(list.length) : t('invitePast.inviteSent', { count: list.length }));
                onInviteSent?.(list);
                setTimeout(() => { setToast(''); onClose(); }, 1400);
            } else {
                setToast(t('meetingInvite.alertInviteFail'));
                setTimeout(() => setToast(''), 2000);
            }
        } catch {
            setToast(t('meetingInvite.alertInviteFail'));
            setTimeout(() => setToast(''), 2000);
        } finally {
            setIsSending(false);
        }
    };

    if (!open) return null;

    return (
        <div className={cls("ifpm-overlay")} onClick={onClose}>
            <div className={cls("ifpm-modal")} onClick={e => e.stopPropagation()}>
                <div className={cls("ifpm-header")}>
                    <button className={cls("ifpm-back")} onClick={selectedMeetingId ? () => setSelectedMeetingId(null) : onClose}>
                        {selectedMeetingId ? '←' : <IcClose size={16} />}
                    </button>
                    <span className={cls("ifpm-title")}>
                        {selectedMeetingId
                            ? (titleStep2 || t('invitePast.selectInvitees'))
                            : (titleStep1 || t('invitePast.pastMemberInvite'))}
                    </span>
                    <span style={{ width: 32 }} />
                </div>

                {/* 1단계: 모임 선택 */}
                {!selectedMeetingId && (
                    <div className={cls("ifpm-body")}>
                        {isLoadingPast && <div className={cls("ifpm-empty")}>{t('common.loading')}</div>}
                        {!isLoadingPast && past.length === 0 && (
                            <div className={cls("ifpm-empty")}>{t('invitePast.noPastMeetings')}</div>
                        )}
                        {!isLoadingPast && past.map(m => {
                            const cached = memberCache[m.id] || [];
                            const preview = cached.slice(0, 4);
                            const extra = cached.length > preview.length ? cached.length - preview.length : 0;
                            return (
                                <div key={m.id} className={cls("ifpm-past-row")} onClick={() => setSelectedMeetingId(m.id)}>
                                    <div className={cls("ifpm-past-main")}>
                                        <div className={cls("ifpm-past-title")}>{m.title}</div>
                                        <div className={cls("ifpm-past-meta")}>
                                            {m.date && <span><IcClock size={13} /> {m.date}</span>}
                                            {m.memberCount != null && <span><IcUsers size={13} /> {t('picker.countPeople', { count: m.memberCount })}</span>}
                                        </div>
                                        {cached.length > 0 && (
                                            <div className={cls("ifpm-past-avatars")}>
                                                {preview.map(p => (
                                                    p.profileImgUrl ? (
                                                        <img key={p.userNo} src={p.profileImgUrl} alt="" className={cls("ifpm-past-avatar")} />
                                                    ) : (
                                                        <div key={p.userNo} className={cls("ifpm-past-avatar ifpm-past-avatar-fallback")} title={p.nickname}>
                                                            {p.nickname[0] || '?'}
                                                        </div>
                                                    )
                                                ))}
                                                {extra > 0 && (
                                                    <div className={cls("ifpm-past-avatar ifpm-past-avatar-more")}>+{extra}</div>
                                                )}
                                                <span className={cls("ifpm-past-names")}>
                                                    {preview.map(p => p.nickname).filter(Boolean).join(', ')}
                                                    {extra > 0 ? ' ' + t('invitePast.othersN', { count: extra }) : ''}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <span className={cls("ifpm-past-arrow")}>›</span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* 2단계: 멤버 체크 */}
                {selectedMeetingId && (
                    <>
                        <div className={cls("ifpm-actions-top")}>
                            <button className={cls("ifpm-toggle-all")} onClick={toggleAll}>
                                {checkedUserNos.size === members.length && members.length > 0 ? t('invitePast.deselectAll') : t('invitePast.selectAll')}
                            </button>
                            <span className={cls("ifpm-count")}>{checkedUserNos.size}/{members.length}</span>
                        </div>
                        <div className={cls("ifpm-body")}>
                            {isLoadingMembers && <div className={cls("ifpm-empty")}>{t('picker.loadingMembers')}</div>}
                            {!isLoadingMembers && members.length === 0 && (
                                <div className={cls("ifpm-empty")}>{t('invitePast.noInvitable')}<br/>{t('invitePast.noInvitableSub')}</div>
                            )}
                            {!isLoadingMembers && members.map(mem => {
                                const checked = checkedUserNos.has(mem.userNo);
                                return (
                                    <div key={mem.userNo} className={cls("ifpm-member-row")} onClick={() => toggleMember(mem.userNo)}>
                                        {mem.profileImgUrl ? (
                                            <img src={mem.profileImgUrl} alt="" className={cls("ifpm-avatar")} />
                                        ) : (
                                            <div className={cls("ifpm-avatar ifpm-avatar-fallback")}>{mem.nickname[0] || '?'}</div>
                                        )}
                                        <div className={cls("ifpm-member-info")}>
                                            <div className={cls("ifpm-member-name")}>
                                                {mem.nickname}
                                                {mem.role === 'CREATOR' && <span className={cls("ifpm-role-tag")}>{t('meetingUpcoming.host')}</span>}
                                            </div>
                                        </div>
                                        <div className={cls(`ifpm-checkbox ${checked ? 'on' : ''}`)}>
                                            {checked && <IcCheck size={13} />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className={cls("ifpm-footer")}>
                            <button
                                className={cls("ifpm-confirm")}
                                disabled={checkedUserNos.size === 0 || isSending}
                                onClick={handleInvite}
                            >
                                {isSending ? t('invitePast.inviting') : t('invitePast.sendInvite', { count: checkedUserNos.size })}
                            </button>
                        </div>
                    </>
                )}

                {toast && <div className={cls("ifpm-toast")}>{toast}</div>}
            </div>
        </div>
    );
};

export default InviteFromPastMeetings;
